// Zustand store for the active-tab selection — the add-in's 3rd store (after
// useAuthStore + useCaseStore). Mirrors the established add-in store pattern.
//
// W5 (single-writer): treated like every other store — `reset()` to the
// default tab on logout, wired into `useAuth.logout()`. `activeTab` is arguably
// UI-only, but the safe + consistent posture is full W5 compliance: a future
// tab could become case-scoped (e.g. "the agent thread for THIS case"), so
// resetting to `sources` on logout costs nothing and avoids a future leak
// (plan §2.3). Pinned by use-tab-store.w5.test.ts.
//
// `useTabStore.activeTab` is the SINGLE source of "which tab is active". No
// router exists (main.tsx has no BrowserRouter) — a feature pane reads
// `useTabStore(s => s.activeTab === 'sources')` to answer "am I active?"; it
// MUST NOT introduce a 2nd tab-selection source (plan §8.2).

import { create } from 'zustand'
import type { TabId } from '@/ui/shell/tab-registry'

export const DEFAULT_TAB: TabId = 'sources'

interface TabState {
  activeTab: TabId
  setTab: (id: TabId) => void
  /** W5 — back to DEFAULT_TAB on logout. */
  reset: () => void
}

export const useTabStore = create<TabState>((set) => ({
  activeTab: DEFAULT_TAB,
  setTab: (id) => set({ activeTab: id }),
  reset: () => set({ activeTab: DEFAULT_TAB }),
}))
